/* eslint-disable jsx-a11y/no-autofocus */
import { entries } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import Delta from 'quill-delta';

import { Avatar, Button, Callout, Paragraph, QuillInput } from '@treams/treams-ui';

import {
	CommentTargetType,
	CreateCommentMutationResult,
	CreateCommentInput,
	useCreateCommentMutation,
} from 'generated/graphql';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import { useLocalStorage } from 'react-use';
import { WarningIcon } from '@treams/treams-icons';

export type CommentComposerProps = {
	targetID: string;
	targetType: CommentTargetType;
	metadata?: Record<string, string>;
	autoFocus?: boolean;
	onCreate: (
		comment: NonNullable<CreateCommentMutationResult['data']>['createComment']['comment'],
	) => void;
	onCancel?: () => void;
	onRefresh?: () => void;
};

const CommentComposer = ({
	targetID,
	targetType,
	metadata,
	autoFocus,
	onCreate,
	onRefresh,
	onCancel,
}: CommentComposerProps) => {
	const { t } = useTranslation();

	const [note, setNote, removeFromLocalStorage] = useLocalStorage<undefined | Delta>(
		`notes-${targetID}`,
		undefined,
	);
	const viewer = useAuthenticatedUser();

	const [loading, setLoading] = useState(false);

	const [create] = useCreateCommentMutation();

	async function onSubmit() {
		setLoading(true);

		const input: CreateCommentInput = {
			targetID,
			targetType,
			content: note,
			metadata:
				metadata &&
				entries(metadata).map(([key, value]) => ({
					key,
					value,
				})),
		};

		const { data } = await create({
			variables: {
				input,
			},
		});

		if (data) {
			onCreate(data.createComment.comment);
		}

		/* Clean up */
		setNote(undefined);
		setLoading(false);
		removeFromLocalStorage();
		onRefresh?.();
	}

	const noteIsEmpty = note == null || (note?.ops.length === 1 && note.ops[0].insert === '\n');

	return (
		<Flex>
			<Box flexGrow={0} flexShrink={0}>
				<Avatar user={viewer} size={32} />
			</Box>
			<Box flexGrow={1} flexShrink={1} ml={2}>
				<Paragraph mb={2}>
					{viewer.firstName} {viewer.lastName}
				</Paragraph>
				<QuillInput autoFocus={autoFocus} value={note} onChange={setNote} />

				{!noteIsEmpty && (
					<Callout mt={2} pl={3} pt={2} small color="yellow" icon={<WarningIcon />}>
						{t('comments.composer.autosave')}
					</Callout>
				)}

				<Flex mt={2}>
					<Button loading={loading} title={t('comments.composer.create')} onClick={onSubmit} />
					{onCancel && (
						<Button
							ml={2}
							title={t('comments.composer.cancel')}
							type="secondary"
							disabled={loading}
							onClick={() => onCancel?.()}
						/>
					)}
				</Flex>
			</Box>
		</Flex>
	);
};

export default CommentComposer;
